/**
 * InfrastructureSection Component
 *
 * Data center infrastructure: cooling plant, BESS, and backup generators.
 * Uses MUI Accordion for 3 sub-sections.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  TextField,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  MenuItem,
  FormControlLabel,
  Switch,
  InputAdornment,
} from '@mui/material';
import {
  Save as SaveIcon,
  ExpandMore as ExpandMoreIcon,
  AcUnit as CoolingIcon,
  BatteryChargingFull as BatteryIcon,
  LocalGasStation as GenIcon,
} from '@mui/icons-material';
import { vortxColors, vortxTypography } from '@vortx/shared-ui';

const COOLING_TYPES = [
  { value: 'air_cooled_chiller', label: 'Air-Cooled Chiller' },
  { value: 'water_cooled_chiller', label: 'Water-Cooled Chiller' },
  { value: 'evaporative', label: 'Evaporative Cooling' },
  { value: 'direct_liquid', label: 'Direct Liquid Cooling' },
  { value: 'rear_door_heat_exchanger', label: 'Rear Door Heat Exchanger' },
];

const FUEL_TYPES = [
  { value: 'diesel', label: 'Diesel' },
  { value: 'natural_gas', label: 'Natural Gas' },
  { value: 'hvo', label: 'HVO (Renewable Diesel)' },
];

const REDUNDANCY_OPTIONS = [
  { value: 'N', label: 'N (no redundancy)' },
  { value: 'N+1', label: 'N+1' },
  { value: '2N', label: '2N' },
  { value: '2N+1', label: '2N+1' },
];

const textFieldSx = {
  '& .MuiOutlinedInput-root': {
    color: vortxColors.white,
    '& fieldset': { borderColor: vortxColors.teal },
    '&:hover fieldset': { borderColor: vortxColors.gold },
    '&.Mui-focused fieldset': { borderColor: vortxColors.gold, borderWidth: 2 },
  },
  '& .MuiInputLabel-root': {
    color: vortxColors.stone,
    '&.Mui-focused': { color: vortxColors.gold },
  },
  '& .MuiFormHelperText-root': {
    color: vortxColors.stone,
  },
  '& .MuiInputAdornment-root': {
    color: vortxColors.gold,
  },
};

const accordionSx = {
  bgcolor: 'rgba(0,0,0,0.2)',
  border: `1px solid ${vortxColors.teal}`,
  '&:before': { display: 'none' },
  mb: 2,
};

function InfrastructureSection({ project, onUpdate }) {
  const [formData, setFormData] = useState({
    // Cooling
    cooling_type: 'air_cooled_chiller',
    chiller_count: '2',
    chiller_capacity_tons: '500',
    economizer_enabled: false,
    tes_enabled: false,
    tes_capacity_kwh: '',
    tes_max_power_kw: '',
    // Battery
    bess_capacity_kwh: '',
    bess_max_power_kw: '',
    bess_efficiency: '0.92',
    bess_min_soc_pct: '10',
    bess_reserve_soc_pct: '20',
    // Backup Gen
    gen_count: '2',
    gen_capacity_kw: '2000',
    gen_fuel_type: 'diesel',
    gen_redundancy: 'N+1',
  });

  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    if (project) {
      setFormData({
        cooling_type: project.cooling_type || 'air_cooled_chiller',
        chiller_count: project.chiller_count || '2',
        chiller_capacity_tons: project.chiller_capacity_tons || '500',
        economizer_enabled: project.economizer_enabled || false,
        tes_enabled: project.tes_enabled || false,
        tes_capacity_kwh: project.tes_capacity_kwh || '',
        tes_max_power_kw: project.tes_max_power_kw || '',
        bess_capacity_kwh: project.bess_capacity_kwh || '',
        bess_max_power_kw: project.bess_max_power_kw || '',
        bess_efficiency: project.bess_efficiency || '0.92',
        bess_min_soc_pct: project.bess_min_soc_pct || '10',
        bess_reserve_soc_pct: project.bess_reserve_soc_pct || '20',
        gen_count: project.gen_count || '2',
        gen_capacity_kw: project.gen_capacity_kw || '2000',
        gen_fuel_type: project.gen_fuel_type || 'diesel',
        gen_redundancy: project.gen_redundancy || 'N+1',
      });
    }
  }, [project]);

  const handleChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
    setSaveSuccess(false);
  };

  const handleToggle = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.checked,
    }));
    setSaveSuccess(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onUpdate(formData);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Failed to save infrastructure config:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Typography
        variant="h5"
        sx={{
          fontFamily: vortxTypography.heading,
          color: vortxColors.white,
          mb: 3,
        }}
      >
        Infrastructure
      </Typography>

      {/* Cooling Plant */}
      <Accordion defaultExpanded sx={accordionSx}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: vortxColors.gold }} />}
          sx={{ '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1 } }}
        >
          <CoolingIcon sx={{ color: vortxColors.gold }} />
          <Typography sx={{ color: vortxColors.white, fontWeight: 600 }}>Cooling Plant</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                select
                label="Cooling Type"
                value={formData.cooling_type}
                onChange={handleChange('cooling_type')}
                sx={textFieldSx}
              >
                {COOLING_TYPES.map((ct) => (
                  <MenuItem key={ct.value} value={ct.value}>{ct.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Chiller Count"
                type="number"
                value={formData.chiller_count}
                onChange={handleChange('chiller_count')}
                sx={textFieldSx}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Chiller Capacity"
                type="number"
                value={formData.chiller_capacity_tons}
                onChange={handleChange('chiller_capacity_tons')}
                InputProps={{
                  endAdornment: <InputAdornment position="end">tons</InputAdornment>,
                }}
                sx={textFieldSx}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.economizer_enabled}
                    onChange={handleToggle('economizer_enabled')}
                    sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: vortxColors.gold } }}
                  />
                }
                label={<Typography sx={{ color: vortxColors.white }}>Economizer</Typography>}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.tes_enabled}
                    onChange={handleToggle('tes_enabled')}
                    sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: vortxColors.gold } }}
                  />
                }
                label={<Typography sx={{ color: vortxColors.white }}>Thermal Energy Storage</Typography>}
              />
            </Grid>
            {formData.tes_enabled && (
              <>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="TES Capacity"
                    type="number"
                    value={formData.tes_capacity_kwh}
                    onChange={handleChange('tes_capacity_kwh')}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">kWh</InputAdornment>,
                    }}
                    sx={textFieldSx}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="TES Max Power"
                    type="number"
                    value={formData.tes_max_power_kw}
                    onChange={handleChange('tes_max_power_kw')}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">kW</InputAdornment>,
                    }}
                    sx={textFieldSx}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Battery Energy Storage */}
      <Accordion defaultExpanded sx={accordionSx}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: vortxColors.gold }} />}
          sx={{ '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1 } }}
        >
          <BatteryIcon sx={{ color: vortxColors.gold }} />
          <Typography sx={{ color: vortxColors.white, fontWeight: 600 }}>Battery Energy Storage</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Capacity"
                type="number"
                value={formData.bess_capacity_kwh}
                onChange={handleChange('bess_capacity_kwh')}
                InputProps={{
                  endAdornment: <InputAdornment position="end">kWh</InputAdornment>,
                }}
                sx={textFieldSx}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Max Power"
                type="number"
                value={formData.bess_max_power_kw}
                onChange={handleChange('bess_max_power_kw')}
                InputProps={{
                  endAdornment: <InputAdornment position="end">kW</InputAdornment>,
                }}
                sx={textFieldSx}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Round-Trip Efficiency"
                type="number"
                value={formData.bess_efficiency}
                onChange={handleChange('bess_efficiency')}
                helperText="0.0 - 1.0"
                sx={textFieldSx}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Min SOC"
                type="number"
                value={formData.bess_min_soc_pct}
                onChange={handleChange('bess_min_soc_pct')}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
                sx={textFieldSx}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Reserve SOC"
                type="number"
                value={formData.bess_reserve_soc_pct}
                onChange={handleChange('bess_reserve_soc_pct')}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
                helperText="SOC reserved for backup during outages"
                sx={textFieldSx}
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Backup Generators */}
      <Accordion defaultExpanded sx={accordionSx}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: vortxColors.gold }} />}
          sx={{ '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1 } }}
        >
          <GenIcon sx={{ color: vortxColors.gold }} />
          <Typography sx={{ color: vortxColors.white, fontWeight: 600 }}>Backup Generators</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Generator Count"
                type="number"
                value={formData.gen_count}
                onChange={handleChange('gen_count')}
                sx={textFieldSx}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Capacity Each"
                type="number"
                value={formData.gen_capacity_kw}
                onChange={handleChange('gen_capacity_kw')}
                InputProps={{
                  endAdornment: <InputAdornment position="end">kW</InputAdornment>,
                }}
                sx={textFieldSx}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                select
                label="Fuel Type"
                value={formData.gen_fuel_type}
                onChange={handleChange('gen_fuel_type')}
                sx={textFieldSx}
              >
                {FUEL_TYPES.map((ft) => (
                  <MenuItem key={ft.value} value={ft.value}>{ft.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                select
                label="Redundancy"
                value={formData.gen_redundancy}
                onChange={handleChange('gen_redundancy')}
                sx={textFieldSx}
              >
                {REDUNDANCY_OPTIONS.map((r) => (
                  <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Save */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={20} sx={{ color: '#000' }} /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving}
          sx={{
            background: `linear-gradient(135deg, ${vortxColors.gold} 0%, #d4a574 100%)`,
            color: '#000',
            fontWeight: 600,
            '&:hover': {
              background: `linear-gradient(135deg, #d4a574 0%, ${vortxColors.gold} 100%)`,
            },
          }}
        >
          Save Changes
        </Button>
        {saveSuccess && <Alert severity="success">Changes saved successfully!</Alert>}
      </Box>
    </Box>
  );
}

export default InfrastructureSection;
